export interface ColumnConfig {
  field: string;
  label: string;
  sortable?: boolean;
  width?: string;
  uncapitalized?: boolean;
  isAvatar?: boolean;
  isCheckbox?: boolean;
  render?: (data: any) => void;
}

export enum TableColumnType {
  Text = 'text',
  Date = 'date',
  Tag = 'tag',
  ColoredText = 'colored-text',
  Link = 'link',
  Toggle = 'toggle',
  Select = 'select',
}

export interface TableColumnConfig {
  field: string;
  label: string;
  sortable?: boolean;
  width?: string;
  /** Pins the column to the left edge while the table scrolls sideways. Opt-in: only the first
   * column of a table wide enough to scroll should set it. */
  sticky?: boolean;
  /** Second header line, muted and not uppercased, under a label that then wraps at word
   * boundaries and stops at two lines. Pair it with `tooltip`, which carries what the clipped
   * header cannot. */
  subLabel?: string;
  /** Full header text, shown as the header's tooltip and read out in place of its two lines. */
  tooltip?: string;
  type?: TableColumnType;
  /** For `tag`/`select` columns: row field holding the precomputed TagState (see TagStateOf helpers). */
  stateField?: string;
  /** For `tag` columns: a row without a value shows an empty cell instead of a dash. */
  blankWhenEmpty?: boolean;
  textColorMap?: Record<string, string>;
  bold?: boolean;
  /** For text columns: row field holding a second, muted line shown under the value. */
  subtitleField?: string;
  /** For text columns: row field holding a Material icon name shown before the value. A row
   * whose field is empty shows the value alone. */
  iconField?: string;
  muted?: boolean;
  /** For `link` columns: builds the routerLink commands array for the cell's row. A row it returns
   *  null for shows its value as plain text. */
  linkFn?: (row: Record<string, unknown>) => unknown[] | null;
  /** For `link` columns: row field holding a second text, shown muted after the link and
   * separated from it by a middle dot, such as the channel beside a device. */
  secondaryField?: string;
  /** For `select` columns: the choices offered in the dropdown menu, each with its own tag state. */
  selectOptions?: { value: string; label: string; state: string }[];
  /** Renders a read-only colored left-border accent on this cell, keyed off another row field
   * (e.g. priority) — purely decorative, not an interactive control. Falls back to no accent
   * when the row's `accentField` value has no entry in the map. */
  accentField?: string;
  accentColorMap?: Record<string, string>;
  /** For `toggle` and `select` columns: when this returns true for a given row, the cell renders
   * read-only instead of interactive — a disabled toggle, or a plain tag with no dropdown (e.g. a
   * config_group whose settings are Xemex-fixed, which the backend refuses to change). Leave unset
   * for the default, always-interactive control. */
  disabledWhen?: (row: Record<string, unknown>) => boolean;
}
